import type { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { type SignedUrl, SortingOrder } from "@/graphql/codegen/graphql"

interface FilesTableProps {
  data: SignedUrl[]
  isLoading: boolean
  columns: ColumnDef<SignedUrl>[]
  error?: Error
  noDataMessage?: string
}

export const FilesTable: FC<FilesTableProps> = ({ data, isLoading, error, columns, noDataMessage }) => {
  const [sortOrder, setSortOrder] = useState<SortingOrder>(SortingOrder.Asc)

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const aFieldGroup = a.file?.fieldGroup ?? ""
      const bFieldGroup = b.file?.fieldGroup ?? ""
      return sortOrder === SortingOrder.Asc
        ? aFieldGroup.localeCompare(bFieldGroup)
        : bFieldGroup.localeCompare(aFieldGroup)
    })
  }, [data, sortOrder])

  const sorting = useMemo(() => {
    const tableSortField = "fileType"
    return [
      {
        id: tableSortField,
        desc: sortOrder !== SortingOrder.Asc,
      },
    ]
  }, [sortOrder])

  const handleSortingChange: OnChangeFn<SortingState> = () => {
    setSortOrder((prevOrder) => (prevOrder === SortingOrder.Asc ? SortingOrder.Desc : SortingOrder.Asc))
  }

  return (
    <AerosBaseTable
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data: sortedData,
          columns,
          state: {
            sorting,
            isLoading,
          },
          onSortingChange: handleSortingChange,
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
        },
      }}
      error={error}
      slotProps={{
        bodyRow: {
          className: "hover:bg-primary-100 cursor-pointer",
        },
      }}
      noDataMessage={noDataMessage ?? "No documents found"}
    />
  )
}
